// Simple Ad Loader - Focus on actually loading ads, not destroying them
class SimpleAdLoader {
  constructor() {
    this.loadedAds = new WeakSet();
  }

  // Check if AdSense is ready
  isAdSenseReady() {
    return typeof window !== 'undefined' && !!window.adsbygoogle;
  }

  // Wait for AdSense to be ready
  waitForAdSense(timeout = 5000) {
    return new Promise((resolve) => {
      if (this.isAdSenseReady()) {
        resolve(true);
        return;
      }

      const startTime = Date.now();
      const checkInterval = setInterval(() => {
        if (this.isAdSenseReady()) {
          clearInterval(checkInterval);
          resolve(true);
        } else if (Date.now() - startTime > timeout) {
          clearInterval(checkInterval);
          resolve(false);
        }
      }, 100);
    });
  }

  // Load a single ad
  async loadAd(adElement) {
    if (!adElement) {
      console.warn('🚀 No ad element provided');
      return false;
    }

    // Check if already loaded via our tracking Set
    if (this.loadedAds.has(adElement)) {
      console.log('🚀 Ad already loaded (tracked), skipping');
      return true;
    }

    // Check if AdSense has already processed this element (covers React re-renders / StrictMode)
    const adStatus = adElement.getAttribute('data-ad-status');
    if (adStatus === 'filled' || adStatus === 'unfilled') {
      console.log('🚀 Ad already processed by AdSense, skipping');
      this.loadedAds.add(adElement);
      return true;
    }

    // Check if the element already has ad content inside it
    if (adElement.children.length > 0 && adElement.innerHTML.trim() !== '') {
      console.log('🚀 Ad element already has content, skipping');
      this.loadedAds.add(adElement);
      return true;
    }

    // Check required attributes
    const client = adElement.dataset.adClient || adElement.getAttribute('data-ad-client');
    const slot = adElement.dataset.adSlot || adElement.getAttribute('data-ad-slot');

    if (!client || !slot) {
      console.warn('🚀 Ad missing required attributes:', { client, slot });
      return false;
    }

    try {
      // Wait for AdSense to be ready
      const adSenseReady = await this.waitForAdSense();
      if (!adSenseReady) {
        console.warn('🚀 AdSense not ready, loading anyway...');
      }

      // Push to AdSense (wrapped to catch "already have ads" errors)
      try {
        (window.adsbygoogle = window.adsbygoogle || []).push(adElement);
      } catch (pushError) {
        // Gracefully handle "All ins elements already have ads" error
        console.warn('🚀 AdSense push warning:', pushError.message);
      }
      this.loadedAds.add(adElement);

      console.log('🚀 Ad loaded successfully:', slot);
      return true;
    } catch (error) {
      console.error('🚀 Ad loading error:', error);
      return false;
    }
  }

  // Load all ads on the page
  async loadAllAds() {
    console.log('🚀 Loading all ads...');

    const adSelectors = [
      '.adsbygoogle-1',
      '.adsbygoogle-2',
      '.adsbygoogle-3',
      '.adsbygoogle-4',
      '.adsbygoogle-5',
      '.adsbygoogle-6',
      '.adsbygoogle-7',
      '.adsbygoogle-8',
      '.adsbygoogle-9',
      '.left-adsbygoogle',
      '.right-adsbygoogle'
    ];

    let loadedCount = 0;

    for (const selector of adSelectors) {
      const elements = document.querySelectorAll(selector);
      for (const element of elements) {
        const success = await this.loadAd(element);
        if (success) loadedCount++;

        // Small delay between ads
        await new Promise(resolve => setTimeout(resolve, 50));
      }
    }

    console.log(`🚀 Loaded ${loadedCount} ads`);
    return loadedCount;
  }

  // Load list ads specifically
  async loadListAds() {
    console.log('🚀 Loading list ads...');

    const listAdElements = document.querySelectorAll('.adsbygoogle-7');
    let loadedCount = 0;

    for (const element of listAdElements) {
      const success = await this.loadAd(element);
      if (success) loadedCount++;

      // Small delay between ads
      await new Promise(resolve => setTimeout(resolve, 100));
    }

    console.log(`🚀 Loaded ${loadedCount} list ads`);
    return loadedCount;
  }
}

// Create singleton instance
const simpleAdLoader = new SimpleAdLoader();

// Make globally available
if (typeof window !== 'undefined') {
  window.simpleAdLoader = simpleAdLoader;
  window.loadAllAdsSimple = () => simpleAdLoader.loadAllAds();
  window.loadListAdsSimple = () => simpleAdLoader.loadListAds();
}

export default simpleAdLoader;
